import { useEffect, useState } from "react";
import { Alert, AlertTitle, Box, CircularProgress, Typography } from "@mui/material";

import { callApi } from "@/dataprovider/misc-apis";
import {
  formatLeaveRangeBreakupLines,
  isLeaveBalanceEndpointMissing,
  leaveRangePreviewApiLocation,
  parseLeaveRangePreviewPayload,
  type LeaveRangePreviewEnabled,
} from "@/containers/staff/shared/leave-balance";
import { toLeaveIsoDate, type EmployeeLeaveType } from "./leave-request-helpers";

type Props = {
  mode?: "office" | "self";
  employeeId?: number | null;
  leaveType?: EmployeeLeaveType | "" | null;
  startDate?: unknown;
  endDate?: unknown;
};

type BreakupState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "hidden" }
  | { status: "ready"; preview: LeaveRangePreviewEnabled }
  | { status: "needType" };

/**
 * On the leave create form: after start/end (+ leave type) are set, show
 * working-day paid/unpaid breakup from the live preview API.
 */
export function LeaveRangeBreakupAlert({
  mode = "office",
  employeeId,
  leaveType,
  startDate,
  endDate,
}: Props) {
  const [state, setState] = useState<BreakupState>({ status: "idle" });

  const startIso = toLeaveIsoDate(startDate);
  const endIso = toLeaveIsoDate(endDate);
  const type = leaveType && (leaveType as unknown as string) !== "" ? leaveType : null;

  useEffect(() => {
    if (!startIso || !endIso) {
      setState({ status: "idle" });
      return;
    }
    if (startIso > endIso) {
      setState({ status: "idle" });
      return;
    }
    if (!type) {
      setState({ status: "needType" });
      return;
    }
    if (mode === "office" && (employeeId == null || employeeId < 1)) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    let location: string;
    try {
      location = leaveRangePreviewApiLocation(mode, employeeId ?? undefined);
    } catch {
      setState({ status: "hidden" });
      return;
    }

    callApi({
      location,
      method: "GET",
      data: {
        startDate: startIso,
        endDate: endIso,
        leaveType: type,
      },
    })
      .then((response) => {
        if (cancelled) return;
        const parsed = parseLeaveRangePreviewPayload(response.data);
        if (!parsed || !parsed.enabled) {
          setState({ status: "hidden" });
          return;
        }
        setState({ status: "ready", preview: parsed });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isLeaveBalanceEndpointMissing(err)) {
          setState({ status: "hidden" });
          return;
        }
        setState({ status: "hidden" });
      });

    return () => {
      cancelled = true;
    };
  }, [employeeId, endIso, mode, startIso, type]);

  if (state.status === "idle" || state.status === "hidden") {
    return null;
  }

  if (state.status === "needType") {
    return (
      <Alert severity="info" sx={{ mb: 1.5, width: "100%" }}>
        Select a leave type to see paid vs unpaid working days for this date range.
      </Alert>
    );
  }

  if (state.status === "loading") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  const lines = formatLeaveRangeBreakupLines(state.preview);
  const severity =
    state.preview.unpaidWorkingDays > 0 && state.preview.isPaidType ? "warning" : "info";

  return (
    <Alert severity={severity} sx={{ mb: 1.5, width: "100%" }}>
      <AlertTitle sx={{ mb: 0.5 }}>{lines.headline}</AlertTitle>
      <Typography variant="body2" component="div">
        {lines.detail}
      </Typography>
    </Alert>
  );
}
